// Formats des études Brésil / États-Unis (copie de web/francia/src/lib/format.ts, en dollars) : séparateur
// décimal selon la langue (virgule fr/es, point en), unités comme /francia/ (M$, Mhl).
import { lang } from './i18n.svelte';

const loc = () => (lang() === 'en' ? 'en-GB' : lang() === 'es' ? 'es-ES' : 'fr-FR');

export function nf(v: number, d = 0): string {
  // es-ES ne groupe pas les milliers sous 10 000 : useGrouping 'always' pour « 1.919 ».
  return v.toLocaleString(loc(), { minimumFractionDigits: d, maximumFractionDigits: d, useGrouping: 'always' } as Intl.NumberFormatOptions);
}
const ok = (v: number | null | undefined): v is number => v != null && Number.isFinite(v);
// « Milliard » dans la langue de l'utilisateur : Md (fr), mil M (es), bn (en).
const bn = (u: string) => (({ es: ' mil M', fr: ' Md', en: ' bn ' } as Record<string, string>)[lang()] ?? ' mil M') + u;

// Sous 100 M$, une décimale (« 99,4 M$ ») : les petites origines gardent un chiffre lisible.
export const usd = (v: number | null | undefined) => !ok(v) ? '—' : Math.abs(v) >= 1e9 ? nf(v / 1e9, 2) + bn('$') : Math.abs(v) >= 1e8 ? nf(v / 1e6, 0) + ' M$' : Math.abs(v) >= 1e6 ? nf(v / 1e6, 1) + ' M$' : nf(v / 1e3, 0) + ' k$';
export const musd = (v: number | null | undefined) => !ok(v) ? '—' : nf(v / 1e6, Math.abs(v) < 1e7 ? 1 : 0) + ' M$';
// Volume : litres → millions d'hectolitres ; sous 1 Mhl, en milliers d'hl.
export const mhl = (l: number | null | undefined) => !ok(l) ? '—' : l >= 1e8 ? nf(l / 1e8, 1) + ' Mhl' : l >= 1e5 ? nf(l / 1e5, 0) + ' khl' : nf(l / 1e2, 0) + ' hl';
export const usdL = (v: number | null | undefined) => !ok(v) ? '—' : nf(v, 2) + ' $/L';
export const pct = (v: number | null | undefined, d = 1) => !ok(v) ? '—' : nf(v, d) + ' %';
// Variation signée, signe moins typographique.
export function sg(v: number | null | undefined, d = 1): string {
  if (!ok(v)) return '—';
  const f = Math.pow(10, d), r = Math.round(v * f) / f;
  return (r > 0 ? '+' : r < 0 ? '−' : '') + nf(Math.abs(r), d) + ' %';
}
// Écart en dollars signé : +39 M$ / −4,6 M$ / −120 k$.
export const dUsd = (v: number) => (v > 0 ? '+' : v < 0 ? '−' : '') + (Math.abs(v) >= 1e7 ? nf(Math.abs(v) / 1e6, 0) + ' M$' : Math.abs(v) >= 1e6 ? nf(Math.abs(v) / 1e6, 1) + ' M$' : nf(Math.abs(v) / 1e3, 0) + ' k$');
export const chg = (a: number | null | undefined, b: number | null | undefined) => (ok(a) && ok(b) && b !== 0 ? (a / b - 1) * 100 : null);
// Classe de couleur d'une variation : neutre entre −0,5 % et +0,5 % (« stable »), rouge seulement pour une baisse.
export const cls = (v: number | null | undefined) => (!ok(v) ? 'flat' : v >= 0.5 ? 'up' : v <= -0.5 ? 'down' : 'flat');
export function monthName(m: number, short = false): string {
  return new Date(Date.UTC(2026, m - 1, 1)).toLocaleDateString(loc(), { month: short ? 'short' : 'long', timeZone: 'UTC' });
}
export const esc = (s: string | null | undefined) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
